import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { ScheduleTimePicker } from "@/components/schedule-time-picker";
import type { Student } from "@contracts/student";
import type { ScheduleRule } from "@contracts/schedule";
import type { TeacherProfile } from "@contracts/teacher";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface StudentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
  student?: Student;
}

export default function StudentFormDialog({ open, onOpenChange, trigger, student }: StudentFormDialogProps) {
  const { toast } = useToast();
  const isEditing = !!student;

  const { data: profile } = useQuery<TeacherProfile>({
    queryKey: ["/api/profile"],
  });

  const [name, setName] = useState("");
  const [gender, setGender] = useState<"Male" | "Female">("Male");
  const [lessonType, setLessonType] = useState<"online" | "offline">("online");
  const [address, setAddress] = useState("");
  const [customAddress, setCustomAddress] = useState(false);
  const [unitRate, setUnitRate] = useState("");
  const [unitCurrency, setUnitCurrency] = useState("USD");
  const [scheduleRules, setScheduleRules] = useState<ScheduleRule[]>([]);

  useEffect(() => {
    if (student) {
      setName(student.name);
      setGender(student.gender as "Male" | "Female");
      setLessonType(student.lessonType as "online" | "offline");
      setAddress(student.address || "");
      setUnitRate(student.unitRate);
      setUnitCurrency(student.unitCurrency);
      setScheduleRules((student.scheduleRules as ScheduleRule[]) || []);
      const teacherAddresses = profile?.addresses || [];
      const isTeacherAddr = teacherAddresses.some(a => a.address === student.address);
      setCustomAddress(!isTeacherAddr && !!student.address);
    } else {
      setName("");
      setGender("Male");
      setLessonType("online");
      setAddress("");
      setCustomAddress(false);
      setUnitRate("");
      setUnitCurrency(profile?.currencies?.[0] || "USD");
      setScheduleRules([]);
    }
  }, [student, open, profile]);

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/students", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      onOpenChange(false);
      toast({ title: "Student added successfully", variant: "success" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("PUT", `/api/students/${student!.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/students"] });
      queryClient.invalidateQueries({ queryKey: ["/api/students", student!.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      onOpenChange(false);
      toast({ title: "Student updated successfully", variant: "success" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function addScheduleRule() {
    setScheduleRules([...scheduleRules, {
      dayOfWeek: 1,
      startTime: "10:00",
      duration: 60,
      recurrenceType: "weekly",
    }]);
  }

  function removeScheduleRule(index: number) {
    setScheduleRules(scheduleRules.filter((_, i) => i !== index));
  }

  function updateScheduleRule(index: number, updates: Partial<ScheduleRule>) {
    setScheduleRules(scheduleRules.map((r, i) => i === index ? { ...r, ...updates } : r));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const derivedLessonDuration =
      scheduleRules.length > 0
        ? Math.max(...scheduleRules.map((r) => r.duration || 60))
        : 60;
    const data = {
      name,
      gender,
      lessonType,
      address: lessonType === "offline" ? address : null,
      unitRate,
      unitCurrency,
      lessonDuration: derivedLessonDuration,
      scheduleRules,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  }

  const teacherAddresses = profile?.addresses || [];
  const currencies = profile?.currencies || ["USD", "EUR", "GBP"];
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Student" : "Add New Student"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Student name"
              required
              data-testid="input-student-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={gender} onValueChange={(v) => setGender(v as any)}>
                <SelectTrigger data-testid="select-gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lesson Type</Label>
              <Select value={lessonType} onValueChange={(v) => setLessonType(v as any)}>
                <SelectTrigger data-testid="select-lesson-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {lessonType === "offline" && (
            <div className="space-y-2">
              <Label>Lesson Address</Label>
              {!customAddress && teacherAddresses.length > 0 ? (
                <div className="space-y-2">
                  <Select value={address} onValueChange={setAddress}>
                    <SelectTrigger data-testid="select-address">
                      <SelectValue placeholder="Choose an address" />
                    </SelectTrigger>
                    <SelectContent>
                      {teacherAddresses.map(a => (
                        <SelectItem key={a.id} value={a.address}>{a.label} - {a.address}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setCustomAddress(true); setAddress(""); }}
                    data-testid="button-custom-address"
                  >
                    Or enter a custom address
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter lesson address"
                    data-testid="input-custom-address"
                  />
                  {teacherAddresses.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { setCustomAddress(false); setAddress(""); }}
                    >
                      Choose from your addresses
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rate">Rate/hr</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                min="0"
                value={unitRate}
                onChange={(e) => setUnitRate(e.target.value)}
                placeholder="25.00"
                required
                data-testid="input-unit-rate"
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={unitCurrency} onValueChange={setUnitCurrency}>
                <SelectTrigger data-testid="select-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-medium">Schedule</Label>
              <Button type="button" variant="outline" size="sm" onClick={addScheduleRule} data-testid="button-add-schedule">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Slot
              </Button>
            </div>
            {scheduleRules.length === 0 && (
              <p className="text-sm text-muted-foreground">No schedule defined. Add time slots for recurring lessons.</p>
            )}
            {scheduleRules.map((rule, i) => (
              <div key={i} className="p-4 rounded-lg border bg-card space-y-3" data-testid={`schedule-rule-${i}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Day</Label>
                    <Select value={String(rule.dayOfWeek)} onValueChange={(v) => updateScheduleRule(i, { dayOfWeek: parseInt(v) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS.map((day, idx) => (
                          <SelectItem key={idx} value={String(idx)}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Start time</Label>
                    <ScheduleTimePicker
                      value={rule.startTime}
                      onChange={(hhmm) => updateScheduleRule(i, { startTime: hhmm })}
                      aria-label="Start time"
                      data-testid={`input-schedule-time-${i}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Duration (minutes)</Label>
                    <Input
                      type="number"
                      min="5"
                      max="300"
                      step="5"
                      value={rule.duration}
                      onChange={(e) => updateScheduleRule(i, { duration: parseInt(e.target.value) || 60 })}
                      data-testid={`input-schedule-duration-${i}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Repeat</Label>
                    <Select value={rule.recurrenceType} onValueChange={(v) => updateScheduleRule(i, { recurrenceType: v as "weekly" | "biweekly" })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Biweekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeScheduleRule(i)}>
                    <Trash2 className="w-4 h-4 text-destructive mr-1.5" />
                    Remove slot
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending} data-testid="button-save-student">
              {isPending ? "Saving..." : isEditing ? "Save Changes" : "Add Student"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
